import { formatMoney } from '@theme/money-formatting';
import { addToCart } from '@theme/puf-cart';

const STATUS_CLEAR_DELAY = 4000;
const LETTER_GROUPS = ['harf_renk', 'harf_boyut'];
const DIGIT_GROUPS = ['rakam_tasarim', 'rakam_boyut'];

/**
 * @typedef {object} Config
 * @property {number} variantId - Variant sold per unit (one character, or one pack).
 * @property {number} unitPrice - Unit price in minor units.
 * @property {'text' | 'packs'} mode
 * @property {number} packSize - Pieces in one pack ("packs" mode).
 * @property {string} letters - Characters that can be ordered as letters.
 * @property {string} digits - Characters that can be ordered as digits.
 * @property {string} moneyFormat
 * @property {string} currency
 * @property {Record<string, number>} addons - Add-on variant ID by its price in minor units.
 * @property {Record<string, string>} labels - Cart property names.
 * @property {Record<string, string>} strings - Texts with `[name]` style placeholders.
 *
 * @typedef {object} Choice
 * @property {string} title - Group heading, used as the cart property name.
 * @property {string} value
 * @property {number} addon - Extra charge in minor units.
 */

/**
 * Fills `[name]` placeholders of a translated text.
 * @param {string} template
 * @param {Record<string, string | number>} values
 * @returns {string}
 */
function fill(template = '', values = {}) {
  return template.replace(/\[(\w+)\]/g, (match, key) => (key in values ? String(values[key]) : match));
}

/**
 * Cake topper builder (blocks/puf-topper.liquid). Counts the chosen letters and digits,
 * shows the total with any extra charges, and adds one cart line for the characters plus
 * one line per extra charge, all carrying the choices as line item properties.
 */
class PufTopper extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;
  /** @type {Config | undefined} */
  #config;
  /** Packs chosen per character in "packs" mode. @type {{ harf: Map<string, number>, rakam: Map<string, number> }} */
  #packs = { harf: new Map(), rakam: new Map() };
  #busy = false;
  #addonWarning = false;

  connectedCallback() {
    const configText = this.querySelector('script[data-config]')?.textContent;
    if (!configText) return;
    this.#config = JSON.parse(configText);

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('input', this.#onInput, { signal });
    this.addEventListener('change', this.#render, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.#render();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    clearTimeout(this.#timer);
  }

  /** @param {Event} event */
  #onInput = (event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.dataset.text) {
      // Upper-case with Turkish rules (i -> İ, ı -> I) while keeping the caret in place.
      const caret = input.selectionStart;
      input.value = input.value.toLocaleUpperCase('tr');
      if (caret !== null) input.setSelectionRange(caret, caret);
    }
    this.#render();
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!button || !this.contains(button)) return;

    if (button.matches('[data-plus], [data-minus]')) {
      const cell = button.closest('[data-char]');
      const kind = button.closest('[data-picker]')?.getAttribute('data-picker');
      const character = cell?.getAttribute('data-char');
      if (!character || (kind !== 'harf' && kind !== 'rakam')) return;

      const packs = this.#packs[kind];
      const next = Math.max(0, (packs.get(character) ?? 0) + (button.matches('[data-plus]') ? 1 : -1));
      if (next > 0) packs.set(character, next);
      else packs.delete(character);
      this.#render();
    } else if (button.matches('[data-submit]')) {
      this.#submit();
    }
  };

  /** @param {number} amount - Minor units. */
  #money(amount) {
    return this.#config ? formatMoney(amount, this.#config.moneyFormat, this.#config.currency) : '';
  }

  /**
   * Reads one part of the order (letters or digits).
   * @param {'harf' | 'rakam'} kind
   * @returns {{ units: number, text: string, unsupported: string[] }}
   */
  #part(kind) {
    const config = this.#config;
    if (!config) return { units: 0, text: '', unsupported: [] };
    const allowed = kind === 'harf' ? config.letters : config.digits;

    if (config.mode === 'packs') {
      const entries = [...this.#packs[kind]].sort(([a], [b]) => allowed.indexOf(a) - allowed.indexOf(b));
      return {
        units: entries.reduce((sum, [, packs]) => sum + packs, 0),
        text: entries.map(([character, packs]) => `${character} × ${packs * config.packSize}`).join(', '),
        unsupported: [],
      };
    }

    const input = this.querySelector(`[data-text="${kind}"]`);
    const text = input instanceof HTMLInputElement ? input.value.trim().replace(/\s+/g, ' ') : '';
    const characters = [...text].filter((character) => character !== ' ');
    return {
      units: characters.filter((character) => allowed.includes(character)).length,
      text,
      unsupported: [...new Set(characters.filter((character) => !allowed.includes(character)))],
    };
  }

  /**
   * The checked choice of each group that applies to the current order.
   * @param {boolean} hasLetters
   * @param {boolean} hasDigits
   * @returns {Record<string, Choice>}
   */
  #choices(hasLetters, hasDigits) {
    /** @type {Record<string, Choice>} */
    const choices = {};
    const groups = [...(hasLetters ? LETTER_GROUPS : []), ...(hasDigits ? DIGIT_GROUPS : [])];

    for (const key of groups) {
      const group = this.querySelector(`[data-group="${key}"]`);
      const checked = group?.querySelector('input:checked');
      if (!(group instanceof HTMLElement) || !(checked instanceof HTMLInputElement)) continue;
      choices[key] = { title: group.dataset.title ?? '', value: checked.value, addon: Number(checked.dataset.addon) || 0 };
    }
    return choices;
  }

  /** Everything the summary and the cart lines need. */
  #order() {
    const config = this.#config;
    if (!config) return null;

    const letters = this.#part('harf');
    const digits = this.#part('rakam');
    const choices = this.#choices(letters.units > 0, digits.units > 0);
    const units = letters.units + digits.units;
    const extras = Object.values(choices).filter((choice) => choice.addon > 0);

    return {
      letters,
      digits,
      choices,
      units,
      extras,
      unsupported: [...letters.unsupported, ...digits.unsupported],
      missingAddon: extras.some((choice) => !config.addons[String(choice.addon)]),
      total: units * config.unitPrice + extras.reduce((sum, choice) => sum + choice.addon, 0),
    };
  }

  #render = () => {
    const config = this.#config;
    const order = this.#order();
    if (!config || !order) return;
    const { strings } = config;

    for (const kind of /** @type {const} */ (['harf', 'rakam'])) {
      const part = kind === 'harf' ? order.letters : order.digits;

      const hint = this.querySelector(`[data-hint="${kind}"]`);
      if (hint instanceof HTMLElement) {
        const allowed = [...(kind === 'harf' ? config.letters : config.digits)].join(' ');
        hint.toggleAttribute('data-warn', part.unsupported.length > 0);
        hint.textContent = part.unsupported.length
          ? fill(strings.unsupported, { chars: part.unsupported.join(' ') })
          : fill(strings.allowed, { chars: allowed });
      }

      for (const cell of this.querySelectorAll(`[data-picker="${kind}"] [data-char]`)) {
        const packs = this.#packs[kind].get(cell.getAttribute('data-char') ?? '') ?? 0;
        cell.toggleAttribute('data-picked', packs > 0);
        const count = cell.querySelector('[data-count]');
        if (count) count.textContent = String(packs * config.packSize);
        const minus = cell.querySelector('[data-minus]');
        if (minus instanceof HTMLButtonElement) minus.disabled = packs === 0;
      }
    }

    const lines = [];
    if (order.units > 0) {
      const label =
        config.mode === 'packs'
          ? fill(strings.packs, { count: order.units, pieces: order.units * config.packSize, price: this.#money(config.unitPrice) })
          : fill(strings.units, { count: order.units, price: this.#money(config.unitPrice) });
      lines.push([label, this.#money(order.units * config.unitPrice)]);
      for (const extra of order.extras) lines.push([`${extra.title}: ${extra.value}`, `+${this.#money(extra.addon)}`]);
    } else {
      lines.push([strings.empty, '']);
    }

    const summary = this.querySelector('[data-summary]');
    summary?.replaceChildren(
      ...lines.map(([name, value]) => {
        const row = document.createElement('div');
        row.className = 'puf-topper__line';
        const term = document.createElement('dt');
        term.textContent = name;
        const detail = document.createElement('dd');
        detail.textContent = value;
        row.append(term, detail);
        return row;
      })
    );

    const total = this.querySelector('[data-total]');
    if (total) total.textContent = this.#money(order.total);

    const button = this.querySelector('[data-submit]');
    if (button instanceof HTMLButtonElement) {
      button.disabled = this.#busy || order.units === 0 || order.unsupported.length > 0 || order.missingAddon;
      button.toggleAttribute('aria-busy', this.#busy);
    }
    if (order.missingAddon !== this.#addonWarning) {
      this.#addonWarning = order.missingAddon;
      this.#announce(order.missingAddon ? strings.noAddon : '', false);
    }
  };

  async #submit() {
    const config = this.#config;
    const order = this.#order();
    if (!config || !order || this.#busy) return;
    if (order.units === 0 || order.unsupported.length > 0 || order.missingAddon) return;

    const { labels } = config;
    const note = this.querySelector('[data-note]');
    const noteText = note instanceof HTMLTextAreaElement ? note.value.trim() : '';

    /** @type {Record<string, string>} */
    const properties = {};
    if (order.letters.units > 0) {
      properties[labels.letters] = order.letters.text;
      for (const key of LETTER_GROUPS) if (order.choices[key]) properties[order.choices[key].title] = order.choices[key].value;
    }
    if (order.digits.units > 0) {
      properties[labels.digits] = order.digits.text;
      for (const key of DIGIT_GROUPS) if (order.choices[key]) properties[order.choices[key].title] = order.choices[key].value;
    }
    if (noteText) properties[labels.note] = noteText;
    // Quantity label shown in the cart instead of a stepper (see snippets/cart-products.liquid).
    properties._sabit =
      config.mode === 'packs'
        ? fill(config.strings.packCount, { count: order.units, pieces: order.units * config.packSize })
        : fill(config.strings.pieces, { count: order.units });

    const orderText = [order.letters.text, order.digits.text].filter(Boolean).join(' / ');
    const items = [
      { id: config.variantId, quantity: order.units, properties },
      ...order.extras.map((extra) => ({
        id: config.addons[String(extra.addon)],
        quantity: 1,
        properties: {
          [labels.option]: `${extra.title}: ${extra.value}`,
          [labels.order]: orderText,
          _sabit: fill(config.strings.pieces, { count: 1 }),
        },
      })),
    ];

    this.#busy = true;
    this.#render();

    try {
      const error = await addToCart(this, items, 'puf-topper');
      if (error === null) {
        this.#reset();
        this.#announce(config.strings.added);
      } else {
        this.#announce(error || config.strings.error);
      }
    } catch {
      this.#announce(config.strings.error);
    } finally {
      this.#busy = false;
      this.#render();
    }
  }

  /** Clears the typed text and chosen packs after a successful add; colour and size choices stay. */
  #reset() {
    for (const input of this.querySelectorAll('[data-text], [data-note]')) {
      if (input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) input.value = '';
    }
    this.#packs.harf.clear();
    this.#packs.rakam.clear();
  }

  /**
   * @param {string} message
   * @param {boolean} [clear] - Remove the message after a few seconds.
   */
  #announce(message, clear = true) {
    const status = this.querySelector('[data-status]');
    if (!status) return;

    status.textContent = message;
    clearTimeout(this.#timer);
    if (clear) {
      this.#timer = setTimeout(() => {
        status.textContent = '';
      }, STATUS_CLEAR_DELAY);
    }
  }
}

if (!customElements.get('puf-topper')) customElements.define('puf-topper', PufTopper);
